export type Language = 'nl' | 'en';
export const LANGUAGES: readonly Language[] = ['nl', 'en'];

/**
 * Alle teksten van de app, per taal. Voeg een sleutel altijd in BEIDE talen toe:
 * i18n.test.ts controleert dat de sleutels en de {plaatsvervangers} overeenkomen.
 * Gebruik {naam} voor variabele delen en vul ze in met tf().
 */
export const TRANSLATIONS = {
  nl: {
    metaTitle: 'DartMaster',
    metaDescription: 'Gratis dartscorebord voor 501, 301, Cricket en Count-Up, met gesproken scores, uitgooi-routes en statistieken. Werkt ook offline.',
    dateLocale: 'nl-NL',

    // Algemeen
    closeBtn: 'Sluiten',
    cancel: 'Annuleren',
    confirm: 'Bevestigen',
    undo: 'Ongedaan maken',
    menuOpen: 'Menu openen',
    menuTitle: 'Menu',
    versionLabel: 'Versie {version}',
    versionBuild: 'build {build}',
    upToDate: 'Je hebt de nieuwste versie',

    // Weergave en voorkeuren
    settingsTitle: 'Weergave',
    modeLight: 'Licht',
    modeDark: 'Donker',
    modeAuto: 'Automatisch',
    languageLabel: 'Taal',
    languageDesc: 'Ook de taal van de stem',
    soundLabel: 'Scores afroepen',
    soundDesc: 'Spreek scores hardop uit',
    soundOn: 'Aan',
    soundOff: 'Uit',
    themeColorLabel: 'Kleurthema',
    themeEmerald: 'Groen',
    themeDartboard: 'Dartbord rood',
    themeAmber: 'Goud',

    // Installeren, offline en updates
    installTitle: 'Voeg toe aan beginscherm',
    installDesc: 'Open de app direct, ook offline.',
    installBtn: 'Installeer',
    installedBadge: 'Geïnstalleerd / App-modus actief',
    installStepIos1: "Tik onderin op 'Delen'",
    installStepIos2: "Kies 'Zet op beginscherm'",
    installGeneric: "Open het browsermenu en kies 'App installeren' of 'Toevoegen aan startscherm'.",
    offlineBanner: 'Offline: de app werkt gewoon door, je gegevens blijven bewaard.',
    backOnline: 'Weer online',
    updateAvailable: 'Nieuwe versie beschikbaar',
    updateNow: 'Vernieuwen',
    updateLater: 'Later',

    // Kopbalk
    logoAlt: 'DartMaster',
    navNewGame: 'Nieuw spel',
    navHistory: 'Geschiedenis',
    navStats: 'Ranglijst',
    gameStopped: 'Partij gestopt',

    // Nieuw spel
    setupTitle: 'Nieuw spel',
    setupSubtitle: 'Kies spelers, speltype en invoer',
    playersLabel: 'Spelers',
    playerCountLabel: 'Aantal spelers',
    playerNameLabel: 'Naam speler {n}',
    playerDefault: 'Speler {n}',
    moveUp: 'Speler {n} eerder aan de beurt',
    moveDown: 'Speler {n} later aan de beurt',
    duplicateName: '"{name}" staat er twee keer in. Geef elke speler een eigen naam.',
    gameTypeLabel: 'Speltype',
    mode901Desc: 'Lang',
    mode501Desc: 'Klassiek',
    mode301Desc: 'Snel',
    modeCountUp: 'Count-Up',
    modeCountUpDesc: 'Optellen',
    modeCricket: 'Cricket',
    modeCricketDesc: '15 t/m Bull',
    countUpTargetLabel: 'Doelscore',
    customTarget: 'Eigen doelscore',
    pointsUnit: '{n} pt',
    outModeLabel: 'Uitgooien',
    outDouble: 'Dubbel uit',
    outSingle: 'Enkel uit',
    outMaster: 'Master uit',
    matchLengthLabel: 'Wedstrijdlengte',
    firstToOne: 'Eerst tot 1 leg',
    firstToMany: 'Eerst tot {n} legs',
    inputMethodLabel: 'Invoer',
    inputPerDart: 'Pijl voor pijl',
    inputPerDartDesc: 'Nauwkeurig',
    inputTurnTotal: 'Totaal per beurt',
    inputTurnTotalDesc: 'Snel',
    startGame: 'Spel beginnen',

    // Scorebord
    countUpTarget: 'Doel: {n} pt',
    firstToShort: 'Eerst tot {n}',
    inputShortDart: 'Pijl',
    inputShortTotal: 'Totaal',
    switchInput: 'Invoer wisselen naar {method}',
    switchInputDisabled: 'Wisselen kan aan het begin van een beurt',
    undoShort: 'Herstel',
    undoLabel: 'Laatste invoer herstellen',
    starterLabel: 'Begint deze leg',
    bust: 'Bust',
    legsAvg: 'Legs · gem',
    remainingToTarget: 'Nog {n} pt ({pct}%)',
    checkoutLabel: 'Uitgooi',
    noCheckout: 'Nog geen uitgooi',
    setupLabel: 'Klaarzetten',
    setupAdvice: 'Zet {n} klaar voor de volgende beurt',
    dartsLeftLabel: 'Nog {score} met {n} pijl(en)',
    first9Label: 'Eerste 9',
    first9Hint: 'Gemiddelde per beurt over de eerste 9 darts van elke leg',
    checkoutRateLabel: 'Uit',
    checkoutRateHint: 'Uitgooien raak / darts op een uitgooi-vak',
    dartsLabel: 'Pijlen',
    routeDouble: 'Gooi voor dubbel uit',
    routeMaster: 'Gooi voor dubbel of triple uit',
    routeSingle: 'Gooi voor enkel uit',
    cricketSubtitle: 'Sluit 15–20 en Bull',
    cricketTargetCol: 'Vak',
    cricketPoints: '{n} pnt',
    cricketMarks: '{name}: {n} van 3 op {target}',

    // Invoer per pijl
    removeDart: 'Wis pijl',
    multDouble: '2× Dubbel',
    multTriple: '3× Triple',
    miss: 'Mis (0)',
    bull: 'Bull (25)',
    doubleBull: 'D-Bull (50)',

    // Invoer per beurt
    thrownScore: 'Gegooide score',
    autoConfirm: 'Bevestigt vanzelf…',
    clearInput: 'Wissen',
    enterScore: 'Invoeren',
    maxScore: 'Maximaal 180',
    impossibleScore: '{n} kun je niet gooien met 3 pijlen',
    invalidScore: 'Ongeldige score (0–180)',
    presetsLabel: 'Snelkeuze',
    deleteDigit: 'Laatste cijfer wissen',
    okBtn: 'OK',

    // Uitgooi-vraag
    checkedOut: 'Uitgegooid met {n}!',
    dartsAtDoubleTitle: 'Darts op de dubbel?',
    howManyDarts: 'Met hoeveel darts?',
    ofWhichDouble: 'Waarvan op de dubbel',

    // Beurtmelding
    turnResult: '{name} · beurt',

    // Leg gewonnen
    legWon: 'Leg binnen!',
    matchWon: 'Wedstrijd gewonnen!',
    matchWonText: 'Gefeliciteerd met de winst!',
    legWonText: 'Tussenstand: {won} van de {total} legs.',
    otherScores: 'Scores van medespelers',
    scoreLeft: '{n} over',
    legsCountOne: '1 leg',
    legsCountMany: '{n} legs',
    nextLeg: 'Volgende leg',
    newMatch: 'Nieuwe partij',
    undoWinningDart: 'Laatste worp herstellen',

    // Geschiedenis
    historyTitle: 'Gespeelde partijen',
    historyEmpty: 'Nog geen partijen opgeslagen',
    historyEmptyDesc: 'Speel een partij uit om hier je uitslagen terug te zien.',
    playedOn: 'Gespeeld op {date}',
    deleteRecord: 'Uitslag verwijderen',
    legsShort: 'Legs',
    avgShort: 'Gem.',
    clearAll: 'Alles wissen',
    recordDeleted: 'Uitslag verwijderd',
    historyCleared: 'Geschiedenis gewist',
    winnerLabel: 'Winnaar: {name}',

    // Ranglijst
    statsTitle: 'Ranglijst en records',
    statsEmpty: 'Nog geen cijfers voor {mode}',
    statsEmptyDesc: 'Speel een partij {mode} om gemiddelden, uitgooien en 180’s te zien.',
    winsOf: '{won} van {played} gewonnen ({pct}%)',
    avgPerTurn: 'Gem. per beurt',
    topTurn: 'Hoogste beurt',
    topCheckout: 'Hoogste uitgooi',
    checkoutPct: 'Uitgooi %',
    total180: '180’s',
    total140: '140+',
    rankLabel: 'Plaats {n}',

    // Stem
    speechBust: '{prefix}Bust! Tussenstand {n} over',
    speech180: '{prefix}Honderdtachtig! Tussenstand {n} over',
    speechScoredLeft: '{prefix}{score} gegooid, tussenstand {n} over',
    speechScoredPoints: '{prefix}{score} gegooid, tussenstand {n} punten',
    speechWinner: 'De winnaar is... {name}!',

    // Statistieken
    analyticsTitle: 'Anonieme statistieken',
    analyticsAsk: 'Mag ik anoniem bijhouden hoe de app gebruikt wordt (bezoeken en duur)? Zo kan ik hem verbeteren.',
    analyticsDesc: 'Anoniem bezoek en gebruiksduur, om de app te verbeteren. Je gegevens in de app blijven op je toestel.',
    analyticsAllow: 'Toestaan',
    analyticsDeny: 'Niet nu',
    analyticsOn: 'Statistieken aan',
    analyticsOff: 'Statistieken uit',
  },
  en: {
    metaTitle: 'DartMaster',
    metaDescription: 'Free darts scoreboard for 501, 301, Cricket and Count-Up, with spoken scores, checkout routes and statistics. Works offline too.',
    dateLocale: 'en-GB',

    closeBtn: 'Close',
    cancel: 'Cancel',
    confirm: 'Confirm',
    undo: 'Undo',
    menuOpen: 'Open menu',
    menuTitle: 'Menu',
    versionLabel: 'Version {version}',
    versionBuild: 'build {build}',
    upToDate: 'You have the latest version',

    settingsTitle: 'Appearance',
    modeLight: 'Light',
    modeDark: 'Dark',
    modeAuto: 'Automatic',
    languageLabel: 'Language',
    languageDesc: 'Also the language of the voice',
    soundLabel: 'Call out scores',
    soundDesc: 'Speak scores out loud',
    soundOn: 'On',
    soundOff: 'Off',
    themeColorLabel: 'Colour theme',
    themeEmerald: 'Green',
    themeDartboard: 'Dartboard red',
    themeAmber: 'Gold',

    installTitle: 'Add to home screen',
    installDesc: 'Open the app instantly, even offline.',
    installBtn: 'Install',
    installedBadge: 'Installed / App mode active',
    installStepIos1: "Tap 'Share' at the bottom",
    installStepIos2: "Choose 'Add to Home Screen'",
    installGeneric: "Open the browser menu and choose 'Install app' or 'Add to home screen'.",
    offlineBanner: 'Offline: the app keeps working and your data stays saved.',
    backOnline: 'Back online',
    updateAvailable: 'New version available',
    updateNow: 'Refresh',
    updateLater: 'Later',

    logoAlt: 'DartMaster',
    navNewGame: 'New game',
    navHistory: 'History',
    navStats: 'Leaderboard',
    gameStopped: 'Game stopped',

    setupTitle: 'New game',
    setupSubtitle: 'Choose players, game type and input',
    playersLabel: 'Players',
    playerCountLabel: 'Number of players',
    playerNameLabel: 'Name of player {n}',
    playerDefault: 'Player {n}',
    moveUp: 'Player {n} throws earlier',
    moveDown: 'Player {n} throws later',
    duplicateName: '"{name}" appears twice. Give every player their own name.',
    gameTypeLabel: 'Game type',
    mode901Desc: 'Long',
    mode501Desc: 'Classic',
    mode301Desc: 'Quick',
    modeCountUp: 'Count-Up',
    modeCountUpDesc: 'Add up',
    modeCricket: 'Cricket',
    modeCricketDesc: '15 to Bull',
    countUpTargetLabel: 'Target score',
    customTarget: 'Custom target score',
    pointsUnit: '{n} pts',
    outModeLabel: 'Checkout',
    outDouble: 'Double out',
    outSingle: 'Single out',
    outMaster: 'Master out',
    matchLengthLabel: 'Match length',
    firstToOne: 'First to 1 leg',
    firstToMany: 'First to {n} legs',
    inputMethodLabel: 'Input',
    inputPerDart: 'Dart by dart',
    inputPerDartDesc: 'Precise',
    inputTurnTotal: 'Turn total',
    inputTurnTotalDesc: 'Fast',
    startGame: 'Start game',

    countUpTarget: 'Target: {n} pts',
    firstToShort: 'First to {n}',
    inputShortDart: 'Dart',
    inputShortTotal: 'Total',
    switchInput: 'Switch input to {method}',
    switchInputDisabled: 'You can switch at the start of a turn',
    undoShort: 'Undo',
    undoLabel: 'Undo last entry',
    starterLabel: 'Starts this leg',
    bust: 'Bust',
    legsAvg: 'Legs · avg',
    remainingToTarget: '{n} pts to go ({pct}%)',
    checkoutLabel: 'Checkout',
    noCheckout: 'No checkout yet',
    setupLabel: 'Set up',
    setupAdvice: 'Leave {n} for your next turn',
    dartsLeftLabel: '{score} left with {n} dart(s)',
    first9Label: 'First 9',
    first9Hint: 'Average per turn over the first 9 darts of each leg',
    checkoutRateLabel: 'Out',
    checkoutRateHint: 'Checkouts hit / darts at a finishing target',
    dartsLabel: 'Darts',
    routeDouble: 'Go for a double out',
    routeMaster: 'Go for a double or treble out',
    routeSingle: 'Go for a single out',
    cricketSubtitle: 'Close 15–20 and Bull',
    cricketTargetCol: 'Target',
    cricketPoints: '{n} pts',
    cricketMarks: '{name}: {n} of 3 on {target}',

    removeDart: 'Remove dart',
    multDouble: '2× Double',
    multTriple: '3× Treble',
    miss: 'Miss (0)',
    bull: 'Bull (25)',
    doubleBull: 'D-Bull (50)',

    thrownScore: 'Score thrown',
    autoConfirm: 'Confirms automatically…',
    clearInput: 'Clear',
    enterScore: 'Enter',
    maxScore: 'Maximum is 180',
    impossibleScore: '{n} cannot be scored with 3 darts',
    invalidScore: 'Invalid score (0–180)',
    presetsLabel: 'Quick picks',
    deleteDigit: 'Delete last digit',
    okBtn: 'OK',

    checkedOut: 'Checked out with {n}!',
    dartsAtDoubleTitle: 'Darts at a double?',
    howManyDarts: 'How many darts?',
    ofWhichDouble: 'Of which at the double',

    turnResult: '{name} · turn',

    legWon: 'Leg won!',
    matchWon: 'Match won!',
    matchWonText: 'Congratulations on the win!',
    legWonText: 'Score: {won} of {total} legs.',
    otherScores: 'Other players',
    scoreLeft: '{n} left',
    legsCountOne: '1 leg',
    legsCountMany: '{n} legs',
    nextLeg: 'Next leg',
    newMatch: 'New match',
    undoWinningDart: 'Undo last dart',

    historyTitle: 'Played matches',
    historyEmpty: 'No matches saved yet',
    historyEmptyDesc: 'Finish a match to see your results here.',
    playedOn: 'Played on {date}',
    deleteRecord: 'Delete result',
    legsShort: 'Legs',
    avgShort: 'Avg.',
    clearAll: 'Clear all',
    recordDeleted: 'Result deleted',
    historyCleared: 'History cleared',
    winnerLabel: 'Winner: {name}',

    statsTitle: 'Leaderboard and records',
    statsEmpty: 'No numbers for {mode} yet',
    statsEmptyDesc: 'Play a {mode} match to see averages, checkouts and 180s.',
    winsOf: '{won} of {played} won ({pct}%)',
    avgPerTurn: 'Avg. per turn',
    topTurn: 'Highest turn',
    topCheckout: 'Highest checkout',
    checkoutPct: 'Checkout %',
    total180: '180s',
    total140: '140+',
    rankLabel: 'Rank {n}',

    speechBust: '{prefix}Bust! Standing: {n} remaining',
    speech180: '{prefix}One hundred and eighty! Standing: {n} remaining',
    speechScoredLeft: '{prefix}{score} scored, standing: {n} remaining',
    speechScoredPoints: '{prefix}{score} scored, standing: {n} points',
    speechWinner: 'The winner is... {name}!',

    // Statistics
    analyticsTitle: 'Anonymous statistics',
    analyticsAsk: 'May I anonymously track how the app is used (visits and duration)? It helps me improve it.',
    analyticsDesc: 'Anonymous visits and usage time, to improve the app. Your data in the app stays on your device.',
    analyticsAllow: 'Allow',
    analyticsDeny: 'Not now',
    analyticsOn: 'Statistics on',
    analyticsOff: 'Statistics off',
  },
} as const;

export type TranslationKey = keyof typeof TRANSLATIONS.nl;

/** Vertaalt een sleutel; ontbreekt hij in de gekozen taal, dan valt hij terug op Nederlands. */
export function t(lang: Language, key: TranslationKey): string {
  const table = TRANSLATIONS[lang] as Record<string, string>;
  return table[key] ?? TRANSLATIONS.nl[key];
}

/** Zoals t(), maar vult {plaatsvervangers} in. Onbekende plaatsvervangers blijven staan. */
export function tf(lang: Language, key: TranslationKey, vars: Record<string, string | number>): string {
  return t(lang, key).replace(/\{(\w+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match));
}
